import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext.tsx';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title }) => {
  const { adminLogout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className="flex h-screen bg-gray-light">
      {/* Sidebar */}
      <div 
        className={`bg-base shadow-lg ${isSidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 ease-in-out`}
      >
        <div className="p-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-8">
            <Link to="/admin" className={`font-bold text-xl font-space-grotesk ${isSidebarOpen ? 'block' : 'hidden'}`}>
              Athenex Admin
            </Link>
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-1 rounded-full hover:bg-gray-base"
            >
              {isSidebarOpen ? '◀️' : '▶️'}
            </button>
          </div>
          
          <nav className="flex-1">
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/admin" 
                  className={`flex items-center p-2 rounded-lg ${isActivePath('/admin') && location.pathname === '/admin' ? 'bg-upload-button-pressed text-black' : 'hover:bg-gray-base'}`}
                >
                  <span className="material-icons">dashboard</span>
                  {isSidebarOpen && <span className="ml-3">Dashboard</span>}
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/users" 
                  className={`flex items-center p-2 rounded-lg ${isActivePath('/admin/users') ? 'bg-upload-button-pressed text-black' : 'hover:bg-gray-base'}`}
                >
                  <span className="material-icons">people</span>
                  {isSidebarOpen && <span className="ml-3">Users</span>}
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/database/stats" 
                  className={`flex items-center p-2 rounded-lg ${isActivePath('/admin/database/stats') ? 'bg-upload-button-pressed text-black' : 'hover:bg-gray-base'}`}
                >
                  <span className="material-icons">storage</span>
                  {isSidebarOpen && <span className="ml-3">Database Stats</span>}
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/database/export" 
                  className={`flex items-center p-2 rounded-lg ${isActivePath('/admin/database/export') ? 'bg-upload-button-pressed text-black' : 'hover:bg-gray-base'}`}
                >
                  <span className="material-icons">download</span>
                  {isSidebarOpen && <span className="ml-3">Export Data</span>}
                </Link>
              </li>
            </ul>
          </nav>
          
          <button 
            onClick={handleLogout} 
            className={`mt-auto flex items-center p-2 rounded-lg text-red-600 hover:bg-red-100`}
          >
            <span className="material-icons">logout</span>
            {isSidebarOpen && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-x-hidden overflow-y-auto">
        <header className="bg-base shadow-md">
          <div className="px-6 py-4">
            <h1 className="text-2xl font-bold font-space-grotesk tracking-wide">{title}</h1>
          </div>
        </header>

        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
